/**
 * Encrypt/decrypt reflection data in localStorage so raw dump is not readable.
 * Uses Web Crypto API (AES-GCM). Key is derived from a constant; source is in code so determined attackers with the bundle can decrypt.
 *
 * Storage is user-scoped: signed-in data lives under ahavibes_reflections_uid_<userId>.
 * Pre-login drafts use ahavibes_reflections_anonymous (plus legacy ahavibes_reflections for migration).
 */

const LEGACY_STORAGE_KEY = "ahavibes_reflections";
const ANON_STORAGE_KEY = "ahavibes_reflections_anonymous";
const PBKDF2_SALT = new Uint8Array([97, 104, 97, 118, 105, 98, 101, 115, 95, 114, 102, 108, 99, 116, 95, 115, 97, 108, 116]);
const PBKDF2_ITERATIONS = 100000;
const KEY_LENGTH = 256;
const IV_LENGTH = 12;
const ALGORITHM = "AES-GCM";

function base64ToBytes(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function bytesToBase64(bytes) {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

async function getKey() {
  const enc = new TextEncoder();
  const passphrase = enc.encode("ahv_reflection_storage_v1");
  const imported = await crypto.subtle.importKey("raw", passphrase, "PBKDF2", false, ["deriveBits", "deriveKey"]);
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: PBKDF2_SALT,
      iterations: PBKDF2_ITERATIONS,
      hash: "SHA-256"
    },
    imported,
    { name: ALGORITHM, length: KEY_LENGTH },
    false,
    ["encrypt", "decrypt"]
  );
}

export async function encryptReflections(plainText) {
  const key = await getKey();
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const enc = new TextEncoder();
  const ciphertext = await crypto.subtle.encrypt(
    { name: ALGORITHM, iv, tagLength: 128 },
    key,
    enc.encode(plainText)
  );
  return JSON.stringify({
    iv: bytesToBase64(iv),
    data: bytesToBase64(new Uint8Array(ciphertext))
  });
}

export async function decryptReflections(encrypted) {
  try {
    const key = await getKey();
    const { iv: ivB64, data: dataB64 } = JSON.parse(encrypted);
    const iv = base64ToBytes(ivB64);
    const data = base64ToBytes(dataB64);
    const dec = await crypto.subtle.decrypt(
      { name: ALGORITHM, iv, tagLength: 128 },
      key,
      data
    );
    return new TextDecoder().decode(dec);
  } catch (e) {
    return null;
  }
}

/** @deprecated Prefer reflectionsKeyForUser — kept for callers that expect the legacy constant. */
export const REFLECTION_STORAGE_KEY = LEGACY_STORAGE_KEY;

export function reflectionsKeyForUser(userId) {
  const id = String(userId || "").trim();
  return id ? `ahavibes_reflections_uid_${id}` : ANON_STORAGE_KEY;
}

async function readEntriesFromKey(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const decrypted = await decryptReflections(raw);
    if (decrypted == null) {
      try {
        return JSON.parse(raw);
      } catch (_) {
        return [];
      }
    }
    return JSON.parse(decrypted);
  } catch (_) {
    return [];
  }
}

/**
 * @param {string} [userId] - When set, only that user's local drafts are read.
 *   When omitted/empty, reads anonymous drafts (and migrates legacy shared key if present).
 */
export async function getStoredReflections(userId) {
  const key = reflectionsKeyForUser(userId);
  const entries = await readEntriesFromKey(key);
  if (userId) return Array.isArray(entries) ? entries : [];

  if (Array.isArray(entries) && entries.length > 0) return entries;

  // One-time fallback for pre-scoping installs.
  const legacy = await readEntriesFromKey(LEGACY_STORAGE_KEY);
  return Array.isArray(legacy) ? legacy : [];
}

/**
 * @param {unknown[]} entries
 * @param {string} [userId]
 */
export async function setStoredReflections(entries, userId) {
  const key = reflectionsKeyForUser(userId);
  const plain = JSON.stringify(Array.isArray(entries) ? entries : []);
  const encrypted = await encryptReflections(plain);
  localStorage.setItem(key, encrypted);
  // After anonymous write, drop legacy shared key so it cannot leak across accounts.
  if (!userId) {
    try {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch (_) {
      /* ignore */
    }
  }
}

/** Clear pre-login / legacy local drafts after they sync to an account. */
export async function clearAnonymousReflections() {
  try {
    localStorage.removeItem(ANON_STORAGE_KEY);
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch (_) {
    /* ignore */
  }
}
